import { useRef } from 'react';
import { KEY_RECOIL } from '@/shared/config';
import { selectIsEmptyData, useReportStore } from '@/entities/report';
import type { BookmarkHrChartHandle } from './bookmarkHrChart';

/**
 * Supplies the ECG bookmark tab with the report identity and chart plumbing it renders from. Reads the tab-2 empty-data flag and the ECG data map from the report store, unpacks study id, profile id, report start and timezone offset out of the report info, and holds the imperative ref to the bookmark HR chart.
 *
 * @returns An object carrying keyRecoil, isEmptyData, studyId, profileId, startReport, timezoneOffset, ecgDataMap, hrChartRef and the handleClickArrowButton handler.
 */
const useEcgBookmark = () => {
  const keyRecoil = KEY_RECOIL.RHYTHM_EVENT;
  const isEmptyData = useReportStore(selectIsEmptyData(keyRecoil));
  // reportInfoState (Recoil, Task 7) is typed {} | IReportInfo - cast to destructure.
  const { studyId, profileId, startReport, timezoneOffset } = useReportStore(
    (s) => s.reportInfo,
  ) as any;
  const ecgDataMap = useReportStore((s) => s.ecgDataMap);
  const hrChartRef = useRef<BookmarkHrChartHandle>(null);

  /**
   * Forwards a day-step request from the bookmark toolbar down to the HR chart. Calls through the chart's imperative handle only while the ref is attached.
   *
   * @param isNext - True to move to the next day, false to move to the previous one.
   * @returns Nothing.
   */
  const handleClickArrowButton = (isNext = false) => {
    if (hrChartRef.current) {
      hrChartRef.current.handleClickArrowButton(isNext);
    }
  };

  return {
    keyRecoil,
    isEmptyData,
    studyId,
    profileId,
    startReport,
    timezoneOffset,
    ecgDataMap,
    hrChartRef,
    handleClickArrowButton,
  };
};

export default useEcgBookmark;
